import {
  Component,
  input,
  OnInit,
  DestroyRef,
  inject,
  signal,
  ViewChild,
  output,
  computed,
  forwardRef,
  effect,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzSplitterModule } from 'ng-zorro-antd/splitter';

import {
  DefinitionControllerService,
  FileManagementControllerService,
} from '@rapider/sdk';
import { AuthStore } from '@rapider/authentication';
import {
  NotificationService,
  BrowserStorageManagementService,
} from '@rapider/services';
import {
  ProjectDataService,
  ProjectDataActions,
  ModelMutationDescriptor,
  selectProjectModelsList,
  extractRapiderApiEntitiesFromHtml,
  matchPageEntitiesWithMutations,
  clearSwrSessionCacheForEntities,
} from '@rapider/project-data';
import { environment } from '@rapider/environments';

import { HtmlPreviewComponent } from '../html-preview/html-preview.component';
import { DynamicPageRendererComponent } from '../dynamic-page-renderer/dynamic-page-renderer.component';
import { PageConfig } from '../dynamic-page-renderer/dynamic-page-renderer.config';
import {
  isAllowedManifestEntity,
  isTrustedAppResourceUrl,
} from '../security/sandbox-security.util';
import { Subscription, firstValueFrom } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

/**
 * @class HtmlDefinitionPreviewComponent
 * @description A wrapper component that fetches raw HTML/JS content from a backend definition file
 * and passes it to the sandbox previewer. It provides hooks to listen to and send messages
 * into the executing iframe for future API data bindings, and orchestrates UI features like modals and splitters.
 */
@Component({
  selector: 'rapider-html-definition-preview',
  standalone: true,
  imports: [
    CommonModule,
    HtmlPreviewComponent,
    NzModalModule,
    NzDrawerModule,
    NzSplitterModule,
    forwardRef(() => DynamicPageRendererComponent),
    // CRITICAL FIX: Removed self-import `forwardRef(() => HtmlDefinitionPreviewComponent)`.
    // Standalone components inherently possess self-reference inside their own templates.
    // Injecting them into the imports array causes the template factory to crash and render blank content.
  ],
  templateUrl: './html-definition-preview.component.html',
  host: { class: 'w-full h-full flex-1 min-h-0 flex flex-col' },
})
export class HtmlDefinitionPreviewComponent implements OnInit {
  private definitionController = inject(DefinitionControllerService);
  private projectDataService = inject(ProjectDataService);
  private authStore = inject(AuthStore);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly notificationService = inject(NotificationService);
  private readonly browserStorage = inject(BrowserStorageManagementService);
  private readonly httpClient = inject(HttpClient);
  private readonly fileManagementController = inject(
    FileManagementControllerService,
  );

  private contentSub?: Subscription;
  private readonly usedEntitiesOnPage = new Set<string>();
  private readonly workspaceModels = this.store.selectSignal(
    selectProjectModelsList,
  );

  /** Reference to the child previewer to enable direct messaging into the iframe. */
  @ViewChild(HtmlPreviewComponent)
  private previewComponent!: HtmlPreviewComponent;

  /** Input: The UUID of the parent definition. */
  public definitionId = input.required<string>();

  /** Input: The specific file name within the definition (e.g., 'customDashboard'). */
  public fileName = input.required<string>();

  /** Optional Input: The name of the definition. If not provided, it will be fetched natively. */
  public definitionName = input<string>();

  /** Optional Input: Routing parameters passed down from the parent viewer. */
  public routeParams = input<Record<string, string>>({});

  /** Optional Input: Live code from an editor to bypass fetching. */
  public liveCode = input<string | undefined>(undefined);

  /** Optional Input: Preloaded data models from parent viewer to avoid redundant findById calls. */
  public dataModels = input<any[]>([]);

  // Track if we should route rapiderApi to legacy Project APIs or new Data Manager
  public appDataModels = signal<any[]>([]);

  /** Input: Passes down the editor mode flag to inject the native trojan-horse builder scripts. */
  public enableEditorSdk = input<boolean>(false);

  /** Input: Enables E2E test mode where backend APIs are strictly mocked. */
  public testMode = input<boolean>(false);

  /** Optional Input: Enables allow-same-origin ONLY when parent developer tools (Builder, Bug Pinning, E2E Runner) need DOM inspection. */
  public allowSameOrigin = input<boolean>(false);

  /** Computed: Effective allowSameOrigin flag passed down to the sandbox iframe. */
  public effectiveAllowSameOrigin = computed(
    () => this.allowSameOrigin() || this.testMode() || this.enableEditorSdk(),
  );

  /** Signal to hold the mock data array when running in testMode. */
  public mockSampleData = signal<any[]>([]);

  /**
   * @property {InputSignal<boolean>} displayBrowserHeader
   * @description Optional configuration input controlling whether the mock browser container toolbar frame is visible.
   * When set to false, it is passed down to hide the visual wrapper frame completely. Defaults to true.
   */
  public displayBrowserHeader = input<boolean>(true);

  /** Optional Input: Base URL for resolving /assets/ references inside preview HTML from CDN or S3. */
  public assetBaseUrl = input<string>('');

  /** Optional Input: Active semantic application theme key (e.g. 'default', 'obsidian-compact'). */
  public appTheme = input<string>('default');

  /** Internal signal to hold the assetBaseUrl resolved from App definition if not explicitly passed */
  public fetchedAssetBaseUrl = signal<string>('');

  /** Computed effective assetBaseUrl (validated against trusted origins) */
  public effectiveAssetBaseUrl = computed(() => {
    const candidate = this.assetBaseUrl() || this.fetchedAssetBaseUrl() || '';
    return candidate && isTrustedAppResourceUrl(candidate) ? candidate : '';
  });

  /** Output: Emits API lifecycle events (request, response, error) for external debuggers. */
  public onApiEvent = output<any>();

  /** Output: Emits Editor events (clicks, hovers, drags) captured by the internal SDK. */
  public onEditorEvent = output<any>();

  /** Output: Exposes the internal iframe element once loaded for drag-and-drop event binding. */
  public onIframeLoad = output<HTMLIFrameElement>();

  // State Signals
  // State Signals
  public htmlContent = signal<string>('');
  public fetchedDefinitionName = signal<string | null>(null);
  public appDefinition = signal<any>(null);
  public isLoading = signal<boolean>(true);
  public error = signal<string | null>(null);

  // --- PAGE MODAL STATE ---
  public isPageModalVisible = signal<boolean>(false);
  public isPageModalLoading = signal<boolean>(false);
  public pageModalConfig = signal<PageConfig | null>(null);
  public pageModalType = signal<string | null>(null);
  public pageModalId = signal<string | null>(null);
  public pageModalPayload = signal<Record<string, any>>({});
  public pageModalTitle = signal<string>('Modal');
  public pageModalWidth = signal<number | string>(800);
  public pageModalFileName = signal<string>('pageTemplate.html');

  // --- PAGE DRAWER STATE ---
  public isPageDrawerVisible = signal<boolean>(false);
  public isPageDrawerLoading = signal<boolean>(false);
  public pageDrawerConfig = signal<PageConfig | null>(null);
  public pageDrawerType = signal<string | null>(null);
  public pageDrawerId = signal<string | null>(null);
  public pageDrawerFileName = signal<string>('pageTemplate.html');
  public pageDrawerPayload = signal<Record<string, any>>({});
  public pageDrawerTitle = signal<string>('Drawer');
  public pageDrawerPlacement = signal<'left' | 'right' | 'top' | 'bottom'>(
    'right',
  );
  public pageDrawerSize = signal<'default' | 'large'>('large');
  public pageDrawerWidth = signal<number | string | undefined>(undefined);

  // --- PAGE SPLITTER STATE ---
  public isPageSplitterVisible = signal<boolean>(false);
  public isPageSplitterLoading = signal<boolean>(false);
  public pageSplitterConfig = signal<PageConfig | null>(null);
  public pageSplitterType = signal<string | null>(null);
  public pageSplitterId = signal<string | null>(null);
  public pageSplitterFileName = signal<string>('pageTemplate.html');
  public pageSplitterPayload = signal<Record<string, any>>({});
  public pageSplitterTitle = signal<string | undefined>(undefined);
  public pageSplitterIcon = signal<string | undefined>(undefined);
  public pageSplitterDefaultSize = signal<number | string>('30%');
  public pageSplitterMin = signal<number | string | undefined>(undefined);
  public pageSplitterMax = signal<number | string | undefined>(undefined);
  public pageSplitterResizable = signal<boolean>(true);
  public pageSplitterLayout = signal<'horizontal' | 'vertical'>('horizontal');

  /** * Computed: Resolves the exact drawer width to prevent template structural re-renders.
   * Maps NG-Zorro's native size strings to their pixel equivalents if no custom width is provided.
   * 'large' = 736px, 'default' = 378px.
   */
  public effectiveDrawerWidth = computed(() => {
    const w = this.pageDrawerWidth();
    if (w !== undefined && w !== null && w !== '') return w;
    return this.pageDrawerSize() === 'large' ? 736 : 378;
  });

  /** Computed title for the sandbox preview header. */
  public displayTitle = computed(() => {
    const name =
      this.definitionName() ||
      this.fetchedDefinitionName() ||
      this.definitionId();
    let fName = this.fileName() || 'pageTemplate.html';

    // Ensure the display title always shows the .html extension for clarity
    if (!fName.endsWith('.html')) {
      fName += '.html';
    }

    return `${name} / ${fName}`;
  });

  /**
   * @constructor
   * @description Sets up reactive effects to natively monitor Angular Signal input changes.
   * This ensures that when the Angular Router reuses the parent dynamic-app-viewer component
   * during a navigation event, the new `definitionId` automatically triggers a fresh API fetch.
   */
  constructor() {
    effect(() => {
      const defId = this.definitionId();
      const fName = this.fileName();
      const dName = this.definitionName();
      const lCode = this.liveCode();
      const providedModels = this.dataModels();
      const providedAssetBaseUrl = this.assetBaseUrl();

      // Untracked block ensures that changing internal loading states inside fetchContent()
      // doesn't accidentally re-trigger this effect recursively.
      untracked(() => {
        this.usedEntitiesOnPage.clear();
        // CRITICAL FIX: Reset structural UI overlay states on navigation.
        // If the user had a splitter or modal open on the previous page, we must forcefully close it
        // when navigating to a new page (reusing this component instance).
        // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError (NG0100) on NzDrawerComponent
        setTimeout(() => {
          this.isPageModalVisible.set(false);
          this.isPageDrawerVisible.set(false);
          this.isPageSplitterVisible.set(false);
        });

        if (Array.isArray(providedModels) && providedModels.length > 0) {
          this.appDataModels.set(providedModels);
        }

        if (defId) {
          const hasParentMetadata =
            (Array.isArray(providedModels) && providedModels.length > 0) &&
            Boolean(providedAssetBaseUrl);

          if (!hasParentMetadata) {
            const filter = JSON.stringify({
              fields: ['name', 'type', 'value'],
            });
            this.definitionController
              .findById({ id: defId, filter: filter as any })
              .subscribe({
                next: (res: any) => {
                  if (res) {
                    this.appDefinition.set(res);
                    if (res.name && !dName) {
                      this.fetchedDefinitionName.set(res.name);
                    }

                    // Determine routing strategy based on App definition flag
                    if (res.type === 'App') {
                      this.appDataModels.set(res.value?.dataModels || []);
                      if (res.value?.assetBaseUrl) {
                        this.fetchedAssetBaseUrl.set(res.value.assetBaseUrl);
                      } else if (
                        Array.isArray(res.value?.versions) &&
                        res.value.versions.length > 0
                      ) {
                        const published = res.value.versions.filter(
                          (v: any) => v.status === 'published',
                        );
                        const latest =
                          published.length > 0
                            ? published[published.length - 1]
                            : res.value.versions[
                                res.value.versions.length - 1
                              ];
                        if (latest?.assetBaseUrl) {
                          this.fetchedAssetBaseUrl.set(latest.assetBaseUrl);
                        } else if (latest?.manifestUrl) {
                          const lastSlash = latest.manifestUrl.lastIndexOf('/');
                          if (lastSlash !== -1) {
                            this.fetchedAssetBaseUrl.set(
                              `${latest.manifestUrl.substring(0, lastSlash)}/assets`,
                            );
                          }
                        }
                      } else if (defId && (res.value?.version || res.version)) {
                        const appVersion = res.value?.version || res.version;
                        this.fetchedAssetBaseUrl.set(
                          `https://rapider.nyc3.cdn.digitaloceanspaces.com/app/${defId}/versions/${appVersion}/assets`,
                        );
                      }
                    } else if (
                      (res.type === 'AppPage' ||
                        res.type === 'AppDynamicPage') &&
                      res.value?.appId
                    ) {
                      // Fetch parent App definition to check the flag
                      const appFilter = JSON.stringify({ fields: ['value'] });
                      this.definitionController
                        .findById({
                          id: res.value.appId,
                          filter: appFilter as any,
                        })
                        .subscribe({
                          next: (appRes: any) => {
                            if (appRes && appRes.value) {
                              this.appDataModels.set(
                                appRes.value.dataModels || [],
                              );
                              if (appRes.value.assetBaseUrl) {
                                this.fetchedAssetBaseUrl.set(
                                  appRes.value.assetBaseUrl,
                                );
                              } else if (
                                Array.isArray(appRes.value.versions) &&
                                appRes.value.versions.length > 0
                              ) {
                                const published = appRes.value.versions.filter(
                                  (v: any) => v.status === 'published',
                                );
                                const latest =
                                  published.length > 0
                                    ? published[published.length - 1]
                                    : appRes.value.versions[
                                        appRes.value.versions.length - 1
                                      ];
                                if (latest?.assetBaseUrl) {
                                  this.fetchedAssetBaseUrl.set(
                                    latest.assetBaseUrl,
                                  );
                                } else if (latest?.manifestUrl) {
                                  const lastSlash =
                                    latest.manifestUrl.lastIndexOf('/');
                                  if (lastSlash !== -1) {
                                    this.fetchedAssetBaseUrl.set(
                                      `${latest.manifestUrl.substring(0, lastSlash)}/assets`,
                                    );
                                  }
                                }
                              } else if (
                                res.value.appId &&
                                (appRes.value?.version || appRes.version)
                              ) {
                                const appVersion =
                                  appRes.value?.version || appRes.version;
                                this.fetchedAssetBaseUrl.set(
                                  `https://rapider.nyc3.cdn.digitaloceanspaces.com/app/${res.value.appId}/versions/${appVersion}/assets`,
                                );
                              }
                            } else {
                              this.appDataModels.set([]);
                            }
                          },
                          error: () => {
                            this.appDataModels.set([]);
                          },
                        });
                    } else {
                      this.appDataModels.set(res.value?.dataModels || []);
                    }
                  }
                },
                error: () => {
                  // Ignore non-fatal metadata fetch errors when parent viewer already handles access
                },
              });
          }

          if (lCode === undefined) {
            this.fetchContent();
          } else {
            this.isLoading.set(false);
          }
        }
      });
    });

    const pendingMutations: ModelMutationDescriptor[] = [];
    this.actions$
      .pipe(
        ofType(ProjectDataActions.notifyModelMutations),
        tap(({ mutations }) => {
          if (Array.isArray(mutations)) {
            pendingMutations.push(...mutations);
          }
        }),
        debounceTime(250),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const batch = pendingMutations.splice(0, pendingMutations.length);
        if (batch.length > 0) {
          this.handleAiModelMutations(batch);
        }
      });
  }

  private handleAiModelMutations(mutations: ModelMutationDescriptor[]): void {
    if (
      this.testMode() ||
      this.enableEditorSdk() ||
      this.liveCode() !== undefined ||
      this.isLoading() ||
      !this.previewComponent
    ) {
      return;
    }

    const pageEntities = new Set<string>(this.usedEntitiesOnPage);
    for (const staticEntity of extractRapiderApiEntitiesFromHtml(
      this.htmlContent(),
    )) {
      pageEntities.add(staticEntity);
    }
    if (pageEntities.size === 0) return;

    const allModels = [
      ...(this.appDataModels() || []),
      ...(this.workspaceModels() || []),
    ];
    const matchedTokens = matchPageEntitiesWithMutations(
      pageEntities,
      mutations,
      allModels,
    );
    if (matchedTokens.length === 0) return;

    clearSwrSessionCacheForEntities(matchedTokens);
    this.previewComponent.reloadSandbox(matchedTokens);
  }

  /**
   * @method ngOnInit
   * @description Standard Angular lifecycle hook. Logic moved to the constructor's reactive `effect()`
   * block to support dynamic input changes during route navigation.
   */
  ngOnInit(): void {
    // Logic migrated to reactive effect.
  }

  /**
   * Fetches the raw HTML content from the server via the SDK.
   */
  private fetchContent(): void {
    this.isLoading.set(true);
    this.error.set(null);

    // CRITICAL FIX: The API expects the `name` without the extension since `extension` is passed separately.
    // If the parent passed 'pageTemplate.html', we strip the '.html' part here so we don't query 'pageTemplate.html.html'.
    let apiName = this.fileName();
    if (apiName.toLowerCase().endsWith('.html')) {
      apiName = apiName.substring(0, apiName.length - 5);
    }

    // We assume the extension is '.html' for this specific previewer
    this.definitionController
      .readFileVersion({
        id: this.definitionId(),
        name: apiName,
        extension: 'html' as any,
      })
      .subscribe({
        next: (content) => {
          this.htmlContent.set(content || '');
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error(
            '[HtmlDefinitionPreview] Failed to load HTML definition file',
            err,
          );
          this.error.set(
            $localize`:@@htmlDefPreview.loadError:Failed to load the requested preview code.`,
          );
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Handler for messages posted outbound from the sandboxed iframe.
   * Can be used in the future to trigger API requests and post results back to the iframe.
   * @param {MessageEvent} event - The message payload from the iframe.
   */
  public handleIframeMessage(event: MessageEvent): void {
    const data = event.data;
    if (!data) return;

    // 1. Cross-Iframe Broadcast Broker
    // Because every HtmlPreviewComponent listens to the parent window's messages,
    // this broadcast sent by one iframe will be caught by all Angular wrappers.
    // We simply forward it back down into this specific wrapper's iframe.
    if (data.type === 'RAPIDER_BROADCAST') {
      // Emit the outbound broadcast event for the debugger
      this.onApiEvent.emit({
        type: 'broadcast_out',
        eventName: data.eventName,
        payload: data.payload,
        timestamp: new Date(),
      });

      // Emit the inbound broadcast event for the debugger (as it loops back down)
      this.onApiEvent.emit({
        type: 'broadcast_in',
        eventName: data.eventName,
        payload: data.payload,
        timestamp: new Date(),
      });

      this.previewComponent.postMessageToIframe({
        type: 'RAPIDER_BROADCAST_RECEIVE',
        eventName: data.eventName,
        payload: data.payload,
      });
      return;
    }

    // 2. Intercept API Requests triggered by the injected window.rapiderApi SDK
    if (data.type === 'RAPIDER_API_REQUEST') {
      const { reqId, operation, entityName, payload } = data;

      if (!operation || !entityName) {
        this.previewComponent.postMessageToIframe({
          type: 'RAPIDER_API_ERROR',
          reqId,
          error: 'Operation and entityName are required.',
        });
        return;
      }

      // Enforce manifest entity allowlist so microapps can only query/mutate their own declared dataModels
      const isWorkflowOp =
        operation === 'triggerWorkflow' || operation === 'startWorkflow';
      if (
        !isWorkflowOp &&
        !isAllowedManifestEntity(entityName, this.appDataModels())
      ) {
        const denyMsg = `Access denied: entity '${entityName}' is not declared in this application's manifest.`;
        this.onApiEvent.emit({
          type: 'error',
          reqId,
          operation,
          entityName,
          error: denyMsg,
          timestamp: new Date(),
        });
        this.previewComponent.postMessageToIframe({
          type: 'RAPIDER_API_ERROR',
          reqId,
          error: denyMsg,
        });
        return;
      }

      if (!isWorkflowOp) {
        this.usedEntitiesOnPage.add(String(entityName));
      }

      // Emit the outgoing request for debugging
      this.onApiEvent.emit({
        type: 'request',
        reqId,
        operation,
        entityName,
        payload,
        timestamp: new Date(),
      });

      // E2E Test Engine Mocking Interceptor
      if (this.testMode()) {
        this.handleMockApiRequest(reqId, operation, entityName, payload);
        return;
      }

      let pathBase = '';
      if (this.authStore.useProjectApiOnDataManagerCalls()) {
        // Format EntityName to standard REST path (e.g. 'ProjectModel' -> 'project-models')
        const kebabName = entityName
          .replace(/([a-z])([A-Z])/g, '$1-$2')
          .replace(/[\s_]+/g, '-')
          .toLowerCase();
        pathBase = kebabName.endsWith('s') ? kebabName : kebabName + 's';
      } else {
        // Use the new dynamic Project Data Manager API
        const models = this.appDataModels();
        const lowerEntity = entityName.toLowerCase();
        const matchedModel = models.find(
          (m: any) =>
            m.dataTableName === entityName ||
            m.datatableName === entityName ||
            m.name === entityName ||
            (m.dataTableName && m.dataTableName.toLowerCase() === lowerEntity) ||
            (m.datatableName && m.datatableName.toLowerCase() === lowerEntity) ||
            (m.name && m.name.toLowerCase() === lowerEntity),
        );
        const tableName =
          matchedModel?.dataTableName ||
          matchedModel?.datatableName ||
          matchedModel?.name ||
          entityName;
        pathBase = `project-data-manager/${tableName}`;
      }

      let method = 'GET';
      let path = `/${pathBase}`;
      let body = null;

      // Map LoopBack repository operations to RESTful endpoints
      switch (operation) {
        case 'create':
          method = 'POST';
          body = payload.body;
          break;
        case 'find':
          method = 'GET';
          if (payload.filter)
            path += `?filter=${encodeURIComponent(JSON.stringify(payload.filter))}`;
          break;
        case 'findById':
          method = 'GET';
          path += `/${payload.id}`;
          if (payload.filter)
            path += `?filter=${encodeURIComponent(JSON.stringify(payload.filter))}`;
          break;
        case 'update':
        case 'updateById':
          method = 'PATCH';
          path += `/${payload.id}`;
          body = payload.body;
          break;
        case 'deleteById':
          method = 'DELETE';
          path += `/${payload.id}`;
          break;
        case 'count':
          method = 'GET';
          path += `/count`;
          if (payload.where)
            path += `?where=${encodeURIComponent(JSON.stringify(payload.where))}`;
          break;
        case 'triggerWorkflow':
        case 'startWorkflow':
          method = 'POST';
          path = '/workflow-management/workflow-events/run-with-jwt';
          body = {
            name: payload.name || entityName,
            payload: payload.payload || {},
            triggerSource: 'RapiderApi',
          };
          break;
        default:
          this.previewComponent.postMessageToIframe({
            type: 'RAPIDER_API_ERROR',
            reqId,
            error: `Unknown operation: ${operation}`,
          });
          return;
      }

      const observerObj = {
        next: (response: any) => {
          // Emit success response for debugging
          this.onApiEvent.emit({
            type: 'success',
            reqId,
            operation,
            entityName,
            response,
            timestamp: new Date(),
          });

          this.previewComponent.postMessageToIframe({
            type: 'RAPIDER_API_RESPONSE',
            reqId,
            payload: response,
          });
        },
        error: (err: any) => {
          console.error('[HtmlDefinitionPreview] Sandbox API Error:', err);

          this.onApiEvent.emit({
            type: 'error',
            reqId,
            operation,
            entityName,
            error:
              err?.error?.error?.message ||
              err?.error?.message ||
              err.message ||
              err.statusText ||
              'Unknown API Error',
            timestamp: new Date(),
          });

          this.previewComponent.postMessageToIframe({
            type: 'RAPIDER_API_ERROR',
            reqId,
            error:
              err?.error?.error?.message ||
              err?.error?.message ||
              err.message ||
              err.statusText ||
              'Unknown API Error',
          });
        },
      };

      // Execute the dynamic API call securely
      this.projectDataService
        .executeProjectApiCall(method, path, body)
        .subscribe(observerObj);
    }
    // 3. Intercept Editor SDK events
    else if (data.type === 'EDITOR_CLICK' || data.type === 'EDITOR_HOVER') {
      this.onEditorEvent.emit(data);
    }
    // 4. Intercept UI Actions (Navigate, Notifications, Modals, Drawers, Splitters)
    else if (data.type === 'RAPIDER_UI_ACTION') {
      const action = data.action;
      if (!action) return;

      const payload = action.payload || {};

      if (action.type === 'navigate' && payload.route) {
        if (typeof window !== 'undefined' && (window as any).__rapiderCommentModeActive) {
          return;
        }
        if (this.isPageDrawerVisible()) {
          this.closePageDrawer();
        }
        if (this.isPageModalVisible()) {
          this.closePageModal();
        }
        let targetSegments: string[] = [];
        if (typeof payload.route === 'string') {
          targetSegments = (payload.route as string).split('/').filter(Boolean);
        } else if (Array.isArray(payload.route)) {
          targetSegments = [...payload.route].filter(Boolean);
        }

        const currentSegments = this.router.url
          .split('?')[0]
          .split('/')
          .filter(Boolean);

        let finalRoute = [];
        if (currentSegments[0] === 'app' && currentSegments.length >= 2) {
          finalRoute = ['/app', currentSegments[1], ...targetSegments];
        } else if (
          currentSegments[0] === 'app-builder' &&
          currentSegments.length >= 3 &&
          currentSegments[2] === 'preview'
        ) {
          finalRoute = [
            '/app-builder',
            currentSegments[1],
            'preview',
            ...targetSegments,
          ];
        } else {
          finalRoute = ['/', ...targetSegments];
        }

        const navExtras: any = {};
        if (payload.queryParams) {
          navExtras.queryParams = payload.queryParams;
        }

        this.router.navigate(finalRoute, navExtras);
      } else if (action.type === 'showNotification' && payload.notification) {
        this.notificationService.createNotification(
          payload.notification.type || 'info',
          'System Message',
          payload.notification.message,
        );
      } else if (action.type === 'showPageModal' && payload.pageModal) {
        const pm = payload.pageModal;
        this.openPageModal(pm.route, pm.payload, pm.title, pm.width);
      } else if (action.type === 'closePageModal') {
        this.closePageModal();
      } else if (action.type === 'showPageDrawer' && payload.pageDrawer) {
        const pd = payload.pageDrawer;
        this.openPageDrawer(
          pd.route,
          pd.payload,
          pd.title,
          pd.placement,
          pd.size,
          pd.width,
        );
      } else if (action.type === 'closePageDrawer') {
        this.closePageDrawer();
      } else if (action.type === 'showPageSplitter' && payload.pageSplitter) {
        const ps = payload.pageSplitter;
        this.openPageSplitter(
          ps.route,
          ps.payload,
          ps.title,
          ps.icon,
          ps.defaultSize,
          ps.min,
          ps.max,
          ps.resizable,
          ps.layout,
        );
      }
    } else {
      // Ignore other window messages (like Webpack HMR or devtools)
      if (data.type !== 'TOGGLE_DARK_MODE') {
        // Silently drop
      }
    }
  }

  // --- OVERLAY & NAVIGATION HANDLERS ---

  /**
   * Helper to locate target page metadata from candidate list of pages.
   */
  private findPageDefinition(routeKey: string, candidatePages: any[]): any {
    if (!Array.isArray(candidatePages) || candidatePages.length === 0) {
      return null;
    }
    const cleanKey = routeKey.trim();
    const lowerKey = cleanKey.toLowerCase();
    const cleanDash = cleanKey.replace(/\//g, '-');
    const lowerDash = cleanDash.toLowerCase();

    return candidatePages.find((p: any) => {
      if (!p) return false;
      const pRoute = (p.route || p.value?.route || '').trim();
      const pKey = (p.key || p.value?.key || '').trim();
      const pId = (p.id || p._id || '').trim();
      const pName = (p.name || p.value?.name || '').trim().toLowerCase();
      const pFile = (p.htmlFilePath || p.value?.htmlFilePath || '').trim();

      return (
        pRoute === cleanKey ||
        pRoute === cleanDash ||
        pKey === cleanKey ||
        pKey === cleanDash ||
        pId === cleanKey ||
        pName === lowerKey ||
        pName === lowerDash ||
        pFile === cleanKey ||
        pFile === `pages/${cleanKey}.html` ||
        pFile === `pages/${cleanDash}.html` ||
        pFile === `${cleanKey}.html` ||
        pFile === `${cleanDash}.html`
      );
    });
  }

  /**
   * Resolves target page metadata from the active App definition.
   */
  private async resolveTargetPage(routeKey: string): Promise<any> {
    let appDef = this.appDefinition();
    if (appDef) {
      const defPages =
        appDef.value?.webPages ||
        appDef.webPages ||
        appDef.value?.pages ||
        appDef.pages ||
        [];
      const match = this.findPageDefinition(routeKey, defPages);
      if (match) return match;
    }

    const defId = this.definitionId();
    if (defId) {
      try {
        const fetchedDef = await firstValueFrom(
          this.definitionController.findById({ id: defId }),
        );
        if (fetchedDef) {
          this.appDefinition.set(fetchedDef);
          const defObj = fetchedDef as any;
          const defPages =
            defObj.value?.webPages ||
            defObj.webPages ||
            defObj.value?.pages ||
            defObj.pages ||
            [];
          const match = this.findPageDefinition(routeKey, defPages);
          if (match) return match;
        }
      } catch (err) {
        console.warn(
          '[HtmlDefinitionPreview] Failed to fetch App definition:',
          err,
        );
      }
    }

    // Fallback: check project applications
    let projectId = this.authStore.activeProject()?.id;
    if (!projectId) {
      projectId =
        this.browserStorage.getItem<string>('activeProjectId') || undefined;
    }
    if (projectId) {
      try {
        const apps = await firstValueFrom(
          this.projectDataService.getProjectApplications(projectId),
        );
        if (Array.isArray(apps)) {
          for (const app of apps) {
            const appPages =
              app.manifest?.pages ||
              app.value?.webPages ||
              app.value?.pages ||
              app.webPages ||
              [];
            const matched = this.findPageDefinition(routeKey, appPages);
            if (matched) return matched;
          }
        }
      } catch (err) {
        console.warn(
          '[HtmlDefinitionPreview] Failed to query project applications:',
          err,
        );
      }
    }

    return null;
  }

  /**
   * Opens an inner page modal, dynamically resolving the requested page definition from App manifest/webPages.
   */
  private async openPageModal(
    routeKey: string,
    payload: any,
    title?: string,
    width?: number | string,
  ): Promise<void> {
    this.pageModalTitle.set(title || 'Page Modal');
    this.pageModalWidth.set(width || 800);
    this.pageModalPayload.set(payload || {});
    this.isPageModalLoading.set(true);

    const targetPage = await this.resolveTargetPage(routeKey);
    if (!targetPage) {
      this.notificationService.createNotification(
        'error',
        'Error',
        `Could not find a page definition matching route: ${routeKey}`,
      );
      this.isPageModalLoading.set(false);
      return;
    }

    const resolvedType =
      targetPage.type ||
      (targetPage.htmlFilePath ||
      targetPage.htmlUrl ||
      !targetPage.dataVariables
        ? 'AppPage'
        : 'AppDynamicPage');

    const fileName =
      targetPage.value?.htmlFilePath ||
      targetPage.htmlFilePath ||
      targetPage.value?.route ||
      targetPage.route ||
      `${routeKey}.html`;

    this.pageModalId.set(this.definitionId());
    this.pageModalType.set(resolvedType);
    this.pageModalFileName.set(fileName);

    if (resolvedType === 'AppPage') {
      setTimeout(() => {
        this.isPageModalVisible.set(true);
        this.isPageModalLoading.set(false);
      }, 0);
      return;
    }

    // AppDynamicPage logic:
    let templateHtml =
      targetPage.template ||
      targetPage.pageTemplateContent ||
      targetPage.value?.template ||
      '';

    if (!templateHtml) {
      const directUrl =
        targetPage.publicUrl ||
        targetPage.htmlUrl ||
        targetPage.url ||
        targetPage.value?.publicUrl ||
        targetPage.value?.htmlUrl ||
        targetPage.value?.url;

      if (directUrl && isTrustedAppResourceUrl(directUrl)) {
        try {
          templateHtml = await firstValueFrom(
            this.httpClient.get(directUrl, { responseType: 'text' }),
          );
        } catch (e) {
          console.warn(
            '[HtmlDefinitionPreview] Failed to load template from public URL:',
            directUrl,
            e,
          );
        }
      }
    }

    if (!templateHtml) {
      let apiName = fileName;
      if (apiName.toLowerCase().endsWith('.html')) {
        apiName = apiName.substring(0, apiName.length - 5);
      }
      try {
        templateHtml = await firstValueFrom(
          this.definitionController.readFileVersion({
            id: this.definitionId(),
            name: apiName,
            extension: 'html' as any,
          }),
        );
      } catch (e) {
        console.warn(
          '[HtmlDefinitionPreview] Failed to read template file version:',
          apiName,
          e,
        );
      }
    }

    const pageConfig: PageConfig = {
      pageId: targetPage.id || targetPage.key || routeKey,
      pageName: targetPage.value?.name || targetPage.name || routeKey,
      route: targetPage.value?.route || targetPage.route || routeKey,
      dataVariables:
        targetPage.value?.dataVariables || targetPage.dataVariables || [],
      dataTransformations:
        targetPage.value?.dataTransformations ||
        targetPage.dataTransformations ||
        [],
      events: targetPage.value?.events || targetPage.events || [],
      template: templateHtml || '',
    };
    this.pageModalConfig.set(pageConfig);
    setTimeout(() => {
      this.isPageModalVisible.set(true);
      this.isPageModalLoading.set(false);
    }, 0);
  }

  public closePageModal(): void {
    this.isPageModalVisible.set(false);
    this.pageModalConfig.set(null);
  }

  /**
   * Opens an inner page drawer, dynamically resolving the requested page definition from App manifest/webPages.
   */
  private async openPageDrawer(
    routeKey: string,
    payload: any,
    title?: string,
    placement?: 'left' | 'right' | 'top' | 'bottom',
    size?: 'default' | 'large',
    width?: number | string,
  ): Promise<void> {
    this.pageDrawerTitle.set(title || 'Drawer View');
    this.pageDrawerPlacement.set(placement || 'right');
    this.pageDrawerSize.set(size || 'large');
    this.pageDrawerWidth.set(width);
    this.pageDrawerPayload.set(payload || {});
    this.isPageDrawerLoading.set(true);

    const targetPage = await this.resolveTargetPage(routeKey);
    if (!targetPage) {
      this.notificationService.createNotification(
        'error',
        'Error',
        `Could not find a page definition matching route: ${routeKey}`,
      );
      this.isPageDrawerLoading.set(false);
      return;
    }

    const resolvedType =
      targetPage.type ||
      (targetPage.htmlFilePath ||
      targetPage.htmlUrl ||
      !targetPage.dataVariables
        ? 'AppPage'
        : 'AppDynamicPage');

    const fileName =
      targetPage.value?.htmlFilePath ||
      targetPage.htmlFilePath ||
      targetPage.value?.route ||
      targetPage.route ||
      `${routeKey}.html`;

    this.pageDrawerId.set(this.definitionId());
    this.pageDrawerType.set(resolvedType);
    this.pageDrawerFileName.set(fileName);

    if (resolvedType === 'AppPage') {
      setTimeout(() => {
        this.isPageDrawerVisible.set(true);
        this.isPageDrawerLoading.set(false);
      }, 0);
      return;
    }

    let templateHtml =
      targetPage.template ||
      targetPage.pageTemplateContent ||
      targetPage.value?.template ||
      '';

    if (!templateHtml) {
      const directUrl =
        targetPage.publicUrl ||
        targetPage.htmlUrl ||
        targetPage.url ||
        targetPage.value?.publicUrl ||
        targetPage.value?.htmlUrl ||
        targetPage.value?.url;

      if (directUrl && isTrustedAppResourceUrl(directUrl)) {
        try {
          templateHtml = await firstValueFrom(
            this.httpClient.get(directUrl, { responseType: 'text' }),
          );
        } catch (e) {
          console.warn(
            '[HtmlDefinitionPreview] Failed to load template from public URL:',
            directUrl,
            e,
          );
        }
      }
    }

    if (!templateHtml) {
      let apiName = fileName;
      if (apiName.toLowerCase().endsWith('.html')) {
        apiName = apiName.substring(0, apiName.length - 5);
      }
      try {
        templateHtml = await firstValueFrom(
          this.definitionController.readFileVersion({
            id: this.definitionId(),
            name: apiName,
            extension: 'html' as any,
          }),
        );
      } catch (e) {
        console.warn(
          '[HtmlDefinitionPreview] Failed to read template file version:',
          apiName,
          e,
        );
      }
    }

    const pageConfig: PageConfig = {
      pageId: targetPage.id || targetPage.key || routeKey,
      pageName: targetPage.value?.name || targetPage.name || routeKey,
      route: targetPage.value?.route || targetPage.route || routeKey,
      dataVariables:
        targetPage.value?.dataVariables || targetPage.dataVariables || [],
      dataTransformations:
        targetPage.value?.dataTransformations ||
        targetPage.dataTransformations ||
        [],
      events: targetPage.value?.events || targetPage.events || [],
      template: templateHtml || '',
    };
    this.pageDrawerConfig.set(pageConfig);
    setTimeout(() => {
      this.isPageDrawerVisible.set(true);
      this.isPageDrawerLoading.set(false);
    }, 0);
  }

  public closePageDrawer(): void {
    this.isPageDrawerVisible.set(false);
    this.pageDrawerConfig.set(null);
  }

  /**
   * Transforms the view into an NZ-Splitter to project a secondary page view side-by-side.
   */
  private async openPageSplitter(
    routeKey: string,
    payload: any,
    title?: string,
    icon?: string,
    defaultSize?: number | string,
    min?: number | string,
    max?: number | string,
    resizable?: boolean,
    layout?: 'horizontal' | 'vertical',
  ): Promise<void> {
    this.pageSplitterTitle.set(title);
    this.pageSplitterIcon.set(icon);
    this.pageSplitterDefaultSize.set(defaultSize ?? '30%');

    this.pageSplitterMin.set(
      min !== undefined && min !== null ? min : undefined,
    );
    this.pageSplitterMax.set(
      max !== undefined && max !== null ? max : undefined,
    );

    this.pageSplitterResizable.set(resizable ?? true);
    this.pageSplitterLayout.set(layout || 'horizontal');
    this.pageSplitterPayload.set(payload || {});

    this.isPageSplitterVisible.set(true);
    this.isPageSplitterLoading.set(true);

    const targetPage = await this.resolveTargetPage(routeKey);
    if (!targetPage) {
      this.notificationService.createNotification(
        'error',
        'Error',
        `Could not find a page definition matching route: ${routeKey}`,
      );
      this.isPageSplitterLoading.set(false);
      return;
    }

    const resolvedType =
      targetPage.type ||
      (targetPage.htmlFilePath ||
      targetPage.htmlUrl ||
      !targetPage.dataVariables
        ? 'AppPage'
        : 'AppDynamicPage');

    const fileName =
      targetPage.value?.htmlFilePath ||
      targetPage.htmlFilePath ||
      targetPage.value?.route ||
      targetPage.route ||
      `${routeKey}.html`;

    this.pageSplitterId.set(this.definitionId());
    this.pageSplitterType.set(resolvedType);
    this.pageSplitterFileName.set(fileName);

    if (resolvedType === 'AppPage') {
      this.isPageSplitterLoading.set(false);
      return;
    }

    let templateHtml =
      targetPage.template ||
      targetPage.pageTemplateContent ||
      targetPage.value?.template ||
      '';

    if (!templateHtml) {
      const directUrl =
        targetPage.publicUrl ||
        targetPage.htmlUrl ||
        targetPage.url ||
        targetPage.value?.publicUrl ||
        targetPage.value?.htmlUrl ||
        targetPage.value?.url;

      if (directUrl && isTrustedAppResourceUrl(directUrl)) {
        try {
          templateHtml = await firstValueFrom(
            this.httpClient.get(directUrl, { responseType: 'text' }),
          );
        } catch (e) {
          console.warn(
            '[HtmlDefinitionPreview] Failed to load template from public URL:',
            directUrl,
            e,
          );
        }
      }
    }

    if (!templateHtml) {
      let apiName = fileName;
      if (apiName.toLowerCase().endsWith('.html')) {
        apiName = apiName.substring(0, apiName.length - 5);
      }
      try {
        templateHtml = await firstValueFrom(
          this.definitionController.readFileVersion({
            id: this.definitionId(),
            name: apiName,
            extension: 'html' as any,
          }),
        );
      } catch (e) {
        console.warn(
          '[HtmlDefinitionPreview] Failed to read template file version:',
          apiName,
          e,
        );
      }
    }

    const pageConfig: PageConfig = {
      pageId: targetPage.id || targetPage.key || routeKey,
      pageName: targetPage.value?.name || targetPage.name || routeKey,
      route: targetPage.value?.route || targetPage.route || routeKey,
      dataVariables:
        targetPage.value?.dataVariables || targetPage.dataVariables || [],
      dataTransformations:
        targetPage.value?.dataTransformations ||
        targetPage.dataTransformations ||
        [],
      events: targetPage.value?.events || targetPage.events || [],
      template: templateHtml || '',
    };
    this.pageSplitterConfig.set(pageConfig);
    this.isPageSplitterLoading.set(false);
  }

  public closePageSplitter(): void {
    this.isPageSplitterVisible.set(false);
    this.pageSplitterConfig.set(null);
  }

  /**
   * Internal E2E Test Engine Interceptor
   * Intercepts `window.rapiderApi` calls when `testMode` is true.
   * Uses `this.mockSampleData()` to return synchronous, lightning-fast mock responses.
   */
  private handleMockApiRequest(
    reqId: string,
    operation: string,
    entityName: string,
    payload: any,
  ): void {
    const mockData = this.mockSampleData() || [];
    // Currently returns dummy responses to simulate success.
    // In a fully built test engine, this would filter against `mockData` based on `payload.filter`.
    let responsePayload: any = [];

    if (operation === 'find') {
      responsePayload = mockData;
    } else if (operation === 'create' || operation === 'update') {
      responsePayload = {
        ...payload?.body,
        id: Math.random().toString(36).substr(2, 9),
      };
    } else if (operation === 'delete') {
      responsePayload = { success: true };
    }

    // Simulate near-instant network latency for realism, yet much faster than real backend.
    setTimeout(() => {
      this.onApiEvent.emit({
        type: 'response',
        reqId,
        payload: responsePayload,
        timestamp: new Date(),
      });
      this.previewComponent.postMessageToIframe({
        type: 'RAPIDER_API_RESPONSE',
        reqId,
        payload: responsePayload,
      });
    }, 50);
  }
}
